export type TimelineAnimatableProperty =
  | "position"
  | "scale"
  | "rotation"
  | "opacity";

export type TimelineMotionPhase = "in" | "out" | "both";

export const TIMELINE_MOTION_PHASES: readonly TimelineMotionPhase[] = [
  "in",
  "out",
  "both",
] as const;

export const TIMELINE_ANIMATABLE_PROPERTIES: readonly TimelineAnimatableProperty[] = [
  "position",
  "scale",
  "rotation",
  "opacity",
] as const;

export const TIMELINE_ANIMATABLE_LABELS: Record<
  TimelineAnimatableProperty,
  string
> = {
  position: "Position",
  scale: "Scale",
  rotation: "Rotation",
  opacity: "Opacity",
};

export type TimelineMotionValue = number | number[];

export type TimelineMotionConfig = {
  property: TimelineAnimatableProperty;
  timing: {
    startFrame: number;
    endFrame: number;
    exitTiming?: {
      startFrame: number;
      endFrame: number;
    };
  };
  phase: TimelineMotionPhase;
  toValue?: TimelineMotionValue;
  deltaValue?: TimelineMotionValue;
};

type LayerTimelineArgs = {
  ip: number;
  op: number;
  timelineOffset?: number;
};

type TimingArgs = LayerTimelineArgs & {
  currentFrame: number;
};

type BuildConfigArgs = TimingArgs & {
  property: TimelineAnimatableProperty;
  currentValue: TimelineMotionValue;
};

type ResolvedAnimationFrames = {
  currentLocalFrame: number;
  otherLocalFrame: number;
  otherIsBefore: boolean;
};

type TimelinePhaseSegment = {
  startFrame: number;
  endFrame: number;
};

type TimelineBoundaryMotionTiming = {
  enter: TimelinePhaseSegment;
  exit: TimelinePhaseSegment;
  singleEnter: TimelinePhaseSegment;
  singleExit: TimelinePhaseSegment;
};

type BuildTimelinePresetTimingArgs = LayerTimelineArgs & {
  phase: TimelineMotionPhase;
  frameRate?: number;
  preferredDurationFrames?: number;
};

const DEFAULT_TIMELINE_PRESET_FRAME_RATE = 30;
const DEFAULT_TIMELINE_PRESET_DURATION_FRAMES = 30;

function resolveAnimationFrames({
  currentFrame,
  ip,
  op,
  timelineOffset = 0,
}: TimingArgs): ResolvedAnimationFrames | null {
  const boundedCurrentFrame = Math.max(ip, Math.min(op, currentFrame));
  const preferredAfterFrame = boundedCurrentFrame + 100;
  const preferredBeforeFrame = boundedCurrentFrame - 100;
  const useAfter = preferredAfterFrame <= op || preferredBeforeFrame < ip;
  const otherFrame = useAfter
    ? Math.min(op, preferredAfterFrame)
    : Math.max(ip, preferredBeforeFrame);

  if (otherFrame === boundedCurrentFrame) {
    return null;
  }

  return {
    currentLocalFrame: boundedCurrentFrame - timelineOffset,
    otherLocalFrame: otherFrame - timelineOffset,
    otherIsBefore: otherFrame < boundedCurrentFrame,
  };
}

function buildLayerBoundaryMotionTiming({
  ip,
  op,
  timelineOffset = 0,
  preferredDurationFrames = DEFAULT_TIMELINE_PRESET_DURATION_FRAMES,
}: LayerTimelineArgs & {
  preferredDurationFrames?: number;
}): TimelineBoundaryMotionTiming {
  const localStart = ip - timelineOffset;
  const localEnd = op - timelineOffset;
  const availableFrames = Math.max(0, localEnd - localStart);
  const singleDuration = Math.min(preferredDurationFrames, availableFrames);
  const splitDuration =
    availableFrames > 0
      ? Math.min(
          preferredDurationFrames,
          Math.max(1, Math.floor(availableFrames / 2))
        )
      : 0;

  return {
    enter: {
      startFrame: localStart,
      endFrame: Math.min(localEnd, localStart + splitDuration),
    },
    exit: {
      startFrame: Math.max(localStart, localEnd - splitDuration),
      endFrame: localEnd,
    },
    singleEnter: {
      startFrame: localStart,
      endFrame: Math.min(localEnd, localStart + singleDuration),
    },
    singleExit: {
      startFrame: Math.max(localStart, localEnd - singleDuration),
      endFrame: localEnd,
    },
  };
}

function toScalar(value: TimelineMotionValue, fallback: number): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (Array.isArray(value) && typeof value[0] === "number") {
    return value[0];
  }

  return fallback;
}

function toVector(value: TimelineMotionValue, fallback: number[]): number[] {
  if (Array.isArray(value) && value.length > 0) {
    return value.slice();
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return [value];
  }

  return fallback.slice();
}

function getTimelinePresetDurationFrames(frameRate?: number): number {
  const resolvedFrameRate =
    typeof frameRate === "number" && Number.isFinite(frameRate) && frameRate > 0
      ? frameRate
      : DEFAULT_TIMELINE_PRESET_FRAME_RATE;

  return Math.max(1, Math.round(resolvedFrameRate));
}

export function buildTimelineMotionConfig({
  property,
  currentValue,
  currentFrame,
  ip,
  op,
  timelineOffset = 0,
}: BuildConfigArgs): TimelineMotionConfig | null {
  const frames = resolveAnimationFrames({
    currentFrame,
    ip,
    op,
    timelineOffset,
  });
  if (!frames) {
    return null;
  }

  const phase = frames.otherIsBefore ? "in" : "out";
  const timing = frames.otherIsBefore
    ? {
        startFrame: frames.otherLocalFrame,
        endFrame: frames.currentLocalFrame,
      }
    : {
        startFrame: frames.currentLocalFrame,
        endFrame: frames.otherLocalFrame,
      };

  if (property === "opacity") {
    return {
      property,
      timing,
      phase,
      toValue: 0,
    };
  }

  if (property === "position") {
    return {
      property,
      timing,
      phase,
      deltaValue: [0, frames.otherIsBefore ? -50 : 50, 0],
    };
  }

  if (property === "rotation") {
    return {
      property,
      timing,
      phase,
      deltaValue: frames.otherIsBefore ? -50 : 50,
    };
  }

  const scaleValue = toVector(currentValue, [100, 100, 100]);
  const factor = frames.otherIsBefore ? 0.5 : 2;

  return {
    property,
    timing,
    phase,
    toValue: scaleValue.map((entry) => entry * factor),
  };
}

export function normalizeTimelineMotionValue(
  value: unknown,
  property: TimelineAnimatableProperty
): TimelineMotionValue {
  if (property === "opacity" || property === "rotation") {
    return toScalar(
      value as TimelineMotionValue,
      property === "opacity" ? 100 : 0
    );
  }

  if (property === "position") {
    return toVector(value as TimelineMotionValue, [0, 0, 0]);
  }

  return toVector(value as TimelineMotionValue, [100, 100, 100]);
}

export function buildTimelinePresetTiming({
  phase,
  ip,
  op,
  timelineOffset = 0,
  frameRate,
  preferredDurationFrames,
}: BuildTimelinePresetTimingArgs): TimelineMotionConfig["timing"] {
  const durationFrames =
    preferredDurationFrames ?? getTimelinePresetDurationFrames(frameRate);
  const fallbackTiming = buildLayerBoundaryMotionTiming({
    ip,
    op,
    timelineOffset,
    preferredDurationFrames: durationFrames,
  });

  if (phase === "in") {
    return fallbackTiming.singleEnter;
  }

  if (phase === "out") {
    return fallbackTiming.singleExit;
  }

  return {
    startFrame: fallbackTiming.enter.startFrame,
    endFrame: fallbackTiming.enter.endFrame,
    exitTiming: {
      startFrame: fallbackTiming.exit.startFrame,
      endFrame: fallbackTiming.exit.endFrame,
    },
  };
}
